import { Navigate, Route, Routes } from 'react-router-dom';
import { adminRouteAccess } from '@rsos/capstone/src/constants/adminRouteAccess';
import NotFound from '@rsos/components/capstone/ErrorBoundary/NotFound';
import AccessGate from '../sections/access_management/app/App';
import IPSecurity from '../sections/access_management/components/IPSecurityManagement';
import ManageSSO from '../sections/access_management/components/ManageSSO';
import RolesManagement from '../sections/access_management/components/RolesManagement';
import Users from '../sections/access_management/components/Users';
import AlertsRoutingGate from '../sections/alerts_routing/app/App';
import AlertsDashboard from '../sections/alerts_routing/components/Dashboard';
import CommunicationGate from '../sections/communication/app/App';
import MessagingAdmin from '../sections/communication/components/MessagingAdmin';
import DatasourceGate from '../sections/datasource/App';
import { DiscrepancyReporting } from '../sections/datasource/pages/discrepancy-reporting/DiscrepancyReporting';
import { MapLayers } from '../sections/datasource/pages/map-layers/MapLayers';
import DataSources from '../sections/datasource/pages/roles';
import IntegrationGate from '../sections/integration_management/app/App';
import Inbound from '../sections/integration_management/components/Inbound';
import Outbound from '../sections/integration_management/components/Outbound';
import KeywordGate from '../sections/keyword/App';
import { Keywords } from '../sections/keyword/pages/keywords/Keywords';
import LandingZonesGate from '../sections/landing_zones/app/App';
import LandingZonesPage from '../sections/landing_zones/app/Main';
import NotificationsGate from '../sections/notifications/app/App';
import SoundControl from '../sections/notifications/components/SoundControl/SoundControl';
import PeerAgenciesGate from '../sections/peer_agencies/app/App';
import PeerAgenciesPage from '../sections/peer_agencies/app/Main';
import PsapGate from '../sections/psap_management/app/App';
import PsapPage from '../sections/psap_management/app/PsapPage';
import RespondersGate from '../sections/responders/app/App';
import RespondersPage from '../sections/responders/app/Main';
import SettingsGate from '../sections/settings/app/App';
import SettingsPage from '../sections/settings/app/SettingsPage';
import IndexRedirect from './IndexRedirect';
import RequireAccess from './RequireAccess';

// Single consolidated route table for the whole /admin area. Each section is a
// parent route whose `element` is its init gate (see the section's `App`); the
// gate renders shared loading/error UI and, once initialized, an <Outlet /> that
// the child routes below render into. Keeping every path here means the full
// admin route map is readable in one place.
//
// Authorization is not in the gates or the sections: every leaf route is wrapped
// in a RequireAccess guard, which resolves its path in adminRouteAccess
// (@rsos/capstone/src/constants/adminRouteAccess) — the same descriptors the
// sidebar reads, so nav visibility and route access cannot drift. The guard's
// path must match a descriptor key; Main.test.tsx walks this tree and fails if a
// page route is unguarded or names a descriptor that does not exist.
//
// RequireAccess sits inside each gate so the section's initialize() has already
// loaded the PSAP that org capabilities come from.
//
// A section whose children need different permissions resolves its index route
// through IndexRedirect rather than naming a default child, so the landing page
// is one the user can actually reach. Bare /admin does the same over the whole
// table.
//
// react-router ranks routes by specificity, so the explicit section segments win
// over the `/admin` route the communication section owns (it handles the
// deprecated `text-from-911` redirect and `messaging`).
//
// One `*` at the bottom serves every path that is not a route. A branch only
// matches when a descendant completes the match, so an unknown sub-path like
// /admin/psap/typo falls through to it rather than needing a copy inside each
// section — which also means it 404s straight away instead of after that
// section's initialize() has run. Note this is deliberately *not* how a path the
// user lacks access to is handled: RequireAccess moves them to a page they can
// reach, because that path does exist.
function Main() {
  return (
    <Routes>
      <Route path="/admin/settings" element={<SettingsGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/settings">
              <SettingsPage />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/notifications" element={<NotificationsGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/notifications">
              <SoundControl />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/data-source" element={<DatasourceGate />}>
        <Route
          index
          element={
            <IndexRedirect
              paths={[
                '/admin/data-source/roles',
                '/admin/data-source/discrepancy-reporting',
                '/admin/data-source/map-layers',
              ]}
            />
          }
        />
        <Route
          path="roles"
          element={
            <RequireAccess path="/admin/data-source/roles">
              <DataSources />
            </RequireAccess>
          }
        />
        <Route
          path="discrepancy-reporting"
          element={
            <RequireAccess path="/admin/data-source/discrepancy-reporting">
              <DiscrepancyReporting />
            </RequireAccess>
          }
        />
        <Route
          path="map-layers"
          element={
            <RequireAccess path="/admin/data-source/map-layers">
              <MapLayers />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/keywords" element={<KeywordGate />}>
        <Route index element={<Navigate to="triggers" />} />
        <Route
          path="triggers"
          element={
            <RequireAccess path="/admin/keywords">
              <Keywords />
            </RequireAccess>
          }
        />
      </Route>

      {/* Roles & Access and Security are capstone-only; that restriction is now
          declared on their descriptors rather than by omitting the routes. */}
      <Route path="/admin/access" element={<AccessGate />}>
        <Route
          index
          element={
            <IndexRedirect
              paths={[
                '/admin/access/users',
                '/admin/access/roles',
                '/admin/access/security',
                '/admin/access/sso',
              ]}
            />
          }
        />
        <Route
          path="users"
          element={
            <RequireAccess path="/admin/access/users">
              <Users />
            </RequireAccess>
          }
        />
        <Route
          path="roles"
          element={
            <RequireAccess path="/admin/access/roles">
              <RolesManagement />
            </RequireAccess>
          }
        />
        <Route
          path="security"
          element={
            <RequireAccess path="/admin/access/security">
              <IPSecurity />
            </RequireAccess>
          }
        />
        <Route
          path="sso"
          element={
            <RequireAccess path="/admin/access/sso">
              <ManageSSO />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/integrations" element={<IntegrationGate />}>
        <Route
          index
          element={
            <IndexRedirect
              paths={[
                '/admin/integrations/inbound',
                '/admin/integrations/outbound',
              ]}
            />
          }
        />
        <Route
          path="inbound"
          element={
            <RequireAccess path="/admin/integrations/inbound">
              <Inbound />
            </RequireAccess>
          }
        />
        <Route
          path="outbound"
          element={
            <RequireAccess path="/admin/integrations/outbound">
              <Outbound />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/psap" element={<PsapGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/psap">
              <PsapPage />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/alerts-routing" element={<AlertsRoutingGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/alerts-routing">
              <AlertsDashboard />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/responders" element={<RespondersGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/responders">
              <RespondersPage />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/landing-zones" element={<LandingZonesGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/landing-zones">
              <LandingZonesPage />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="/admin/share-and-chat" element={<PeerAgenciesGate />}>
        <Route
          index
          element={
            <RequireAccess path="/admin/share-and-chat">
              <PeerAgenciesPage />
            </RequireAccess>
          }
        />
      </Route>
      <Route
        path="/admin/peer-agencies"
        element={<Navigate to="/admin/share-and-chat" replace />}
      />

      {/* Communication owns the /admin catch-all: it handles messaging + the
          deprecated text-from-911 redirect. Any unmatched /admin/* path
          resolves here. */}
      <Route path="/admin" element={<CommunicationGate />}>
        {/* Bare /admin is not a page. Send the user to the first admin route
            they can reach — the same resolution the top-nav Admin link uses, so
            the two agree — and only 404 when there is genuinely nowhere in
            /admin to put them. */}
        <Route
          index
          element={
            <IndexRedirect
              paths={Object.keys(adminRouteAccess)}
              fallback={<NotFound />}
            />
          }
        />
        <Route
          path="text-from-911"
          element={<Navigate to="/admin/messaging" replace />}
        />
        <Route
          path="messaging"
          element={
            <RequireAccess path="/admin/messaging">
              <MessagingAdmin />
            </RequireAccess>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default Main;
